/**
 * Phase 4: Composable for asset generation with SSE (Server-Sent Events)
 * 
 * This composable encapsulates all EventSource/SSE logic for AI asset generation,
 * making it fully testable and reusable across components.
 */

import { onDestroy } from 'svelte';
import type { ReleaseClient } from '../api/client';
import type { AssetRequest, AssetProgressEvent, AssetCompleteEvent, AssetErrorEvent } from '../types/release';

export interface AssetGenerationCallbacks {
	onProgress?: (message: string) => void;
	onComplete?: (results: { imageUrl: string | null; audioUrl: string | null; videoUrl: string | null }) => void;
	onError?: (error: Error) => void;
}

export interface AssetGenerationOptions {
	client: ReleaseClient;
	callbacks?: AssetGenerationCallbacks;
}

/**
 * Composable for managing asset generation with SSE
 * 
 * @example
 * ```ts
 * const assetGen = useAssetGeneration({
 *   client: new ReleaseClient(),
 *   callbacks: {
 *     onProgress: (msg) => console.log(msg),
 *     onComplete: (results) => console.log('Done!', results),
 *     onError: (err) => console.error(err)
 *   }
 * });
 * 
 * // Start generation
 * await assetGen.startGeneration(assetRequest);
 * 
 * // Cleanup happens automatically on component destroy
 * ```
 */
export function useAssetGeneration(options: AssetGenerationOptions) {
	const { client, callbacks } = options;
	
	let eventSource: EventSource | null = null;

	/**
	 * Clean up EventSource connection
	 */
	function cleanup() {
		if (eventSource) {
			eventSource.close();
			eventSource = null;
		}
	}

	/**
	 * Automatically cleanup on component destroy
	 */
	onDestroy(() => {
		cleanup();
	});

	/**
	 * Open EventSource connection for a job
	 */
	function openEventSource(jobId: string) {
		cleanup(); // Close any existing connection
		
		callbacks?.onProgress?.('Connecting to status stream...');
		eventSource = client.stream(`/api/assets/status/${jobId}`);

		eventSource.onopen = () => {
			callbacks?.onProgress?.('Connected to status stream');
		};

		eventSource.onmessage = (event: MessageEvent) => {
			const data: AssetProgressEvent = JSON.parse(event.data);
			callbacks?.onProgress?.(data.message);
		};

		eventSource.addEventListener('complete', (event: Event) => {
			const data: AssetCompleteEvent = JSON.parse((event as MessageEvent).data);
			cleanup();
			callbacks?.onComplete?.({
				imageUrl: data.results.imageUrl ?? null,
				audioUrl: data.results.audioUrl ?? null,
				videoUrl: data.results.videoUrl ?? null
			});
		});

		eventSource.addEventListener('error_event', (event: Event) => {
			const data: AssetErrorEvent = JSON.parse((event as MessageEvent).data);
			cleanup();
			callbacks?.onError?.(new Error(data.error));
		});

		eventSource.onerror = () => {
			callbacks?.onProgress?.('Connection lost');
		};
	}

	/**
	 * Start asset generation
	 */
	async function startGeneration(request: AssetRequest): Promise<void> {
		callbacks?.onProgress?.('Starting asset generation job...');
		
		try {
			const response = await client.generateAssets(request);
			
			if (!response.jobId) {
				throw new Error('Failed to start asset generation job - no job ID received');
			}
			
			openEventSource(response.jobId);
		} catch (error) {
			const errorMessage = error instanceof Error 
				? error.message 
				: 'Unknown error occurred during asset generation';
			callbacks?.onError?.(new Error(errorMessage));
		}
	}

	/**
	 * Manually stop generation and cleanup
	 */
	function stopGeneration() {
		cleanup();
	}

	return {
		startGeneration,
		stopGeneration,
		cleanup
	};
}
